import { Injectable } from '@angular/core';
import { AuthenticationResult, BrowserCacheLocation, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AUTH_CONSOLE_MESSAGES, AUTH_ERROR_CODES, AUTH_PROVIDER_TYPES, AUTH_STORAGE_KEYS } from '../shared/constants';
import { StorageUtil } from '../shared/utils';
export type IdentityProviderType = 'EntraExternalId' | 'EntraId' | 'Unknown';

@Injectable({
    providedIn: 'root'
})
export class IdentityProviderService {
    private _internalMsalInstance: IPublicClientApplication | null = null;
    private _externalMsalInstance: IPublicClientApplication | null = null;
    private _internalInitialized = false;
    private _externalInitialized = false;

    private _currentIdentityProvider = new BehaviorSubject<IdentityProviderType>(
        this.loadIdentityProviderFromStorage()
    );
    private _redirectResult = new ReplaySubject<AuthenticationResult | null>(1);

    redirectResult$: Observable<AuthenticationResult | null> = this._redirectResult.asObservable();

    get currentIdentityProviderValue(): IdentityProviderType {
        return this._currentIdentityProvider.value;
    }

    constructor() {
        this.initializeFromStorage().catch(error => {
            console.warn(AUTH_CONSOLE_MESSAGES.INIT_STORAGE_ERROR, error);
        });
    }

    getActiveInstance(): Promise<IPublicClientApplication | null> {
        switch (this._currentIdentityProvider.value) {
            case AUTH_PROVIDER_TYPES.ENTRA_ID:
                return this.getOrCreateInternalInstance();
            case AUTH_PROVIDER_TYPES.ENTRA_EXTERNAL_ID:
                return this.getOrCreateExternalInstance();
            default:
                return Promise.resolve(null);
        }
    }

    clearIdentityProvider(): void {
        this._currentIdentityProvider.next(AUTH_PROVIDER_TYPES.UNKNOWN);
        this.clearActiveAccounts();
        StorageUtil.removeItem(AUTH_STORAGE_KEYS.IDENTITY_PROVIDER_TYPE);
        this._redirectResult.next(null);
    }

    clearActiveAccounts(): void {
        if (this._internalMsalInstance && this._internalInitialized) {
            this._internalMsalInstance.setActiveAccount(null);
        }

        if (this._externalMsalInstance && this._externalInitialized) {
            this._externalMsalInstance.setActiveAccount(null);
        }
    }

    async setIdentityProvider(providerType: IdentityProviderType): Promise<void> {
        this._currentIdentityProvider.next(providerType);
        this.saveIdentityProviderType(providerType);
        await this.clearInactiveProviderAccount(providerType);
    }

    async getOrCreateInternalInstance(): Promise<IPublicClientApplication> {
        if (!this._internalMsalInstance) {
            this._internalMsalInstance = this.createMsalInstance(environment.entraId);
        }

        if (!this._internalInitialized) {
            await this._internalMsalInstance.initialize();
            this._internalInitialized = true;
        }

        return this._internalMsalInstance;
    }

    async getOrCreateExternalInstance(): Promise<IPublicClientApplication> {
        if (!this._externalMsalInstance) {
            this._externalMsalInstance = this.createMsalInstance(environment.entraExternalId);
        }

        if (!this._externalInitialized) {
            await this._externalMsalInstance.initialize();
            this._externalInitialized = true;
        }

        return this._externalMsalInstance;
    }

    private async initializeFromStorage(): Promise<void> {
        const providerType = this._currentIdentityProvider.value;

        if (providerType !== AUTH_PROVIDER_TYPES.UNKNOWN) {
            const instance = await this.getActiveInstance();
            if (instance) {
                await this.handleRedirectPromise(instance);
            }
        }
        else {
            if (await this.tryInitializeInternalInstance()) {
                return;
            }
            else {
                await this.tryInitializeExternalInstance();
            }
        }
    }

    private async tryInitializeInternalInstance(): Promise<boolean> {
        const internalInstance = await this.getOrCreateInternalInstance();
        if (!internalInstance) {
            return false;
        }

        const internalAccounts = internalInstance.getAllAccounts();

        if (internalAccounts.length > 0) {
            this._currentIdentityProvider.next(AUTH_PROVIDER_TYPES.ENTRA_ID);
            this.saveIdentityProviderType(AUTH_PROVIDER_TYPES.ENTRA_ID);
            await this.handleRedirectPromise(internalInstance);
            return true;
        }

        return false;
    }

    private async tryInitializeExternalInstance(): Promise<boolean> {
        const externalInstance = await this.getOrCreateExternalInstance();
        if (!externalInstance) {
            return false;
        }

        const externalAccounts = externalInstance.getAllAccounts();

        if (externalAccounts.length > 0) {
            this._currentIdentityProvider.next(AUTH_PROVIDER_TYPES.ENTRA_EXTERNAL_ID);
            this.saveIdentityProviderType(AUTH_PROVIDER_TYPES.ENTRA_EXTERNAL_ID);
            await this.handleRedirectPromise(externalInstance);
            return true;
        }

        return false;
    }

    private loadIdentityProviderFromStorage(): IdentityProviderType {
        const stored = StorageUtil.getItem(AUTH_STORAGE_KEYS.IDENTITY_PROVIDER_TYPE);

        if (stored === AUTH_PROVIDER_TYPES.ENTRA_ID || stored === AUTH_PROVIDER_TYPES.ENTRA_EXTERNAL_ID) {
            return stored;
        }

        return AUTH_PROVIDER_TYPES.UNKNOWN;
    }

    private saveIdentityProviderType(identityProviderType: IdentityProviderType): void {
        if (identityProviderType !== AUTH_PROVIDER_TYPES.UNKNOWN) {
            StorageUtil.setItem(AUTH_STORAGE_KEYS.IDENTITY_PROVIDER_TYPE, identityProviderType);
        }
        else {
            StorageUtil.removeItem(AUTH_STORAGE_KEYS.IDENTITY_PROVIDER_TYPE);
        }
    }

    private createMsalInstance(config: { clientId: string; authority: string; redirectUri: string; }): IPublicClientApplication {
        // MSAL doesn't support cookies, so map to localStorage or sessionStorage
        const cacheLocation = StorageUtil.isLocalStorageAvailable()
            ? BrowserCacheLocation.LocalStorage
            : BrowserCacheLocation.SessionStorage;

        return new PublicClientApplication({
            auth: {
                clientId: config.clientId,
                authority: config.authority,
                redirectUri: config.redirectUri,
                navigateToLoginRequestUrl: true
            },
            cache: {
                cacheLocation: cacheLocation
            }
        });
    }

    private async handleRedirectPromise(instance: IPublicClientApplication): Promise<void> {
        try {
            const authResult = await instance.handleRedirectPromise();
            if (authResult) {
                instance.setActiveAccount(authResult.account);
            }

            this._redirectResult.next(authResult);
        }
        catch (error: any) {
            console.error(AUTH_CONSOLE_MESSAGES.REDIRECT_PROMISE_ERROR, error);

            if (error?.errorCode === AUTH_ERROR_CODES.AUTHORITY_MISMATCH || error?.message?.includes(AUTH_ERROR_CODES.AUTHORITY_MISMATCH)) {
                console.warn(AUTH_CONSOLE_MESSAGES.AUTHORITY_MISMATCH_WARNING);
                this.clearIdentityProvider();
            }
        }
    }

    private async clearInactiveProviderAccount(activeProviderType: IdentityProviderType): Promise<void> {
        if (activeProviderType !== AUTH_PROVIDER_TYPES.ENTRA_ID && this._internalMsalInstance && this._internalInitialized) {
            this._internalMsalInstance.setActiveAccount(null);
        }

        if (activeProviderType !== AUTH_PROVIDER_TYPES.ENTRA_EXTERNAL_ID && this._externalMsalInstance && this._externalInitialized) {
            this._externalMsalInstance.setActiveAccount(null);
        }
    }
}
